import { emitHintSignal } from '@flobi/asset-library/hints';
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { Button, IconButton, NumberField, PopUpButton, Slider } from '../../ui/controls';
import { tip } from '../../ui/tooltip';
import { beginHold } from '../../ui/hold';
import { hexToHsv, hexToRgb, hsvToHex, rgbToHex, type Cmyk, type Hsv } from '@flobi/artwork-core';
import { usePress } from '../press';
import { useKit } from '../work';
import { hexOf, inkFromPercent, inkOf, inkPercent, pressHex, shownHex, typedInk } from '../ink';
import { brandStanding, deltaE, distinctColours, SAME } from './colourChoice';

/**
 * The colour picker: one way to pick, and the colours worth picking under it.
 *
 * Tarek's pick A of four (2026-10-01, https://claude.ai/artifact/4DtqKVyVHYDWUL7gmCrREF),
 * after he asked for the old one — Procreate's five tabs (Disc, Classic, Harmony, Value,
 * Palettes) — to be reworked. Three of those set one colour three ways, Harmony was a
 * curiosity and Palettes lived in one browser; Value said the colour as HSB, RGB and hex at
 * once with two fields both called "B". Now, top to bottom:
 *
 *   the square and the hue bar (what Figma, Apple, Canva and Express all use), the opacity
 *   bar where the colour has one, and the eyedropper beside them;
 *   the numbers in ONE format, chosen from a menu (Hex, RGB, HSB; in a CMYK file the ink
 *   comes first), with the opacity beside them;
 *   what the colour is to the brand: its name, or "Off-brand" with the nearest brand colour
 *   one press away;
 *   the brand's colours (shades folded until asked for; its gradients when a gradient is
 *   being made), the colours this file already paints with, and Recent — each kept once,
 *   near-twins included.
 *
 * The square stays a square rather than Procreate's disc because it says exactly what it
 * does: across is how much colour, down is how dark.
 *
 * In a CMYK file the panel picks in ink. Ink typed is kept exactly as typed; a colour picked
 * any other way becomes the ink the press makes of it and comes back as what that ink looks
 * like — so what is shown is what prints. Every swatch here is drawn as it will print.
 */

/**
 * Where the picker's other colours come from, given by the editor page: the colours the
 * file paints with, and a way to take one off the page where the browser has no eyedropper
 * of its own.
 */
export interface ColourSources {
  /** The file's colours, most used first. Asked once, when a picker opens. */
  fileColours(): string[];
  /** The next press on the canvas takes the colour under it. */
  eyedrop?: (done: (hex: string) => void) => void;
}

export const ColourSourcesContext = createContext<ColourSources | null>(null);

type Format = 'hex' | 'rgb' | 'hsb' | 'cmyk';

const RECENTS_KEY = 'artwork.colour.recents';
const FORMAT_KEY = 'artwork.colour.format';
const SHADES_KEY = 'artwork.colour.shades';
/** How many of the file's colours show before "+n" opens the rest. */
const FILE_FIRST = 11;

/** The browser's own eyedropper (Chrome, Edge): it takes a colour from anywhere on the screen. */
export type NativeEyeDropper = new () => { open(): Promise<{ sRGBHex: string }> };

export function ColourPanel({
  value,
  ink,
  onChange,
  alpha = 1,
  onAlpha,
  header,
  above,
  onBrandGradient,
  onShader,
  title,
  showing,
  onNone,
  onClose,
}: {
  value: string;
  /** The ink the colour was typed as, in a CMYK file; otherwise the press's ink for it is shown. */
  ink?: Cmyk;
  /** The colour chosen, and in a CMYK file the ink it stands for. */
  onChange: (hex: string, ink?: Cmyk) => void;
  /** The colour's opacity, 0–1. Without `onAlpha` the colour has none to set here, and none is shown. */
  alpha?: number;
  onAlpha?: (alpha: number) => void;
  /** What leads the top row: a fill's or stroke's kind (Solid · Gradient). Else the title. */
  header?: ReactNode;
  /** Between the top row and the square: a gradient's own controls. */
  above?: ReactNode;
  /** A gradient being made: the brand's gradients are offered, and this applies one. */
  onBrandGradient?: (gradient: { name: string; from: string; to: string; angle: number }) => void;
  /** Fill the selection with a Lumens shader instead; absent where a shader means nothing. */
  onShader?: () => void;
  /** What is being coloured, said at the top when there is no `header`: "Fill", "Shadow". */
  title?: string;
  /** Whether that paint is showing at all, which is what None turns off. */
  showing: boolean;
  /** Absent where "none" is not a colour the thing can have. */
  onNone?: () => void;
  onClose?: () => void;
}) {
  const [recents, setRecents] = useState<string[]>(() => distinctColours(read<string[]>(RECENTS_KEY, [])));
  const sources = useContext(ColourSourcesContext);
  // Asked once, when the picker opens: a long file is a long count, and a drag across the
  // square redraws this panel on every move.
  const [inFile] = useState<string[]>(() => sources?.fileColours() ?? []);
  const [allOfFile, setAllOfFile] = useState(false);
  const [shades, setShades] = useState<boolean>(() => read(SHADES_KEY, false));

  // The hue and saturation are the panel's own, and are read again from the colour only
  // when it changes from outside. Worked out from the hex each time, black forgot its
  // saturation and every grey its hue — dragging to black and back came up white or red,
  // and the hue bar did nothing while the colour was grey (QA 2026-09-24, paint-F11).
  const [own, setOwn] = useState(() => ({ hex: value.toLowerCase(), hsv: hexToHsv(value) }));
  let hsv = own.hsv;
  if (own.hex !== value.toLowerCase()) {
    const seen = hexToHsv(value);
    // A grey says nothing about hue, and black nothing about saturation: keep ours.
    hsv = { h: seen.s === 0 || seen.v === 0 ? own.hsv.h : seen.h, s: seen.v === 0 ? own.hsv.s : seen.s, v: seen.v };
    setOwn({ hex: value.toLowerCase(), hsv });
  }

  // A colour joins the recent row when it is CHOSEN — a drag let go, a field committed, a
  // swatch pressed, the picker closed on a change — never on every value it passes
  // through. Every in-between shade of one drag used to push out the ten colours actually
  // used, and merely opening the picker or choosing another stop added one too
  // (QA 2026-09-24, paint-F8). A colour too close to tell from one already there replaces it.
  const showingNow = useRef(showing);
  showingNow.current = showing;
  const pending = useRef<string | null>(null);
  const remember = (hex: string) => {
    pending.current = null;
    if (!showingNow.current) return;
    const one = hex.toLowerCase();
    const next = [one, ...read<string[]>(RECENTS_KEY, []).filter((other) => deltaE(other, one) >= SAME)].slice(0, 10);
    write(RECENTS_KEY, next);
    setRecents(distinctColours(next));
    // In a file with a brand, a colour chosen off it or on it (the line's Use, a brand dot): the
    // hint that offers the nearest brand colour counts the one and stops at the other (lib/hints).
    const said = kit ? brandStanding(one, named) : null;
    if (said?.kind === 'brand') emitHintSignal('colour.brandPicked');
    else if (said) emitHintSignal('colour.offBrand');
  };
  const settle = () => {
    if (pending.current) remember(pending.current);
  };
  const settleRef = useRef(settle);
  settleRef.current = settle;
  // Closing the picker settles a change still in hand (a slider moved with the keyboard).
  useEffect(() => () => settleRef.current(), []);

  const kit = useKit();
  const press = usePress();
  /** A colour picked on screen, as the file takes it: in a CMYK file, the press's ink for it. */
  const out = (hex: string): { hex: string; ink?: Cmyk } => (press ? pressHex(press, hex) : { hex });
  /** Swatches drawn as they will print, in a CMYK file. */
  const look = (hex: string) => shownHex(press, hex);
  const inkNow = press ? inkOf(press, { ...hexToRgbUnit(value), ...(ink ? { cmyk: ink } : {}) }) : null;

  const set = (next: Hsv) => {
    const chosen = out(hsvToHex(next));
    // The hand stays where it is; only the colour it gives is the press's.
    setOwn({ hex: chosen.hex.toLowerCase(), hsv: next });
    pending.current = chosen.hex;
    onChange(chosen.hex, chosen.ink);
  };
  /** A colour given whole (typed): chosen the moment it is given. */
  const give = (hex: string) => {
    // A colour typed by hand (Hex, RGB): the hints offer the eyedropper after a few (lib/hints).
    emitHintSignal('colour.typed');
    const chosen = out(hex);
    pending.current = chosen.hex;
    onChange(chosen.hex, chosen.ink);
  };
  /** Ink typed, in a CMYK file: kept exactly as typed. */
  const giveInk = (typed: Cmyk) => {
    if (!press) return;
    const hex = hexOf(typedInk(press, typed));
    pending.current = hex;
    onChange(hex, typed);
  };
  /** A swatch pressed, or a colour taken off the page. */
  const pick = (hex: string) => {
    const chosen = out(hex);
    onChange(chosen.hex, chosen.ink);
    remember(chosen.hex);
  };

  // One format at a time. In a CMYK file the ink comes first, because that is what prints.
  const formats: readonly (readonly [Format, string])[] = press ? [['cmyk', 'CMYK'], ['hex', 'Hex'], ['hsb', 'HSB']] : [['hex', 'Hex'], ['rgb', 'RGB'], ['hsb', 'HSB']];
  const [kept, setKept] = useState<Format>(() => read<Format>(FORMAT_KEY, 'hex'));
  const format: Format = formats.some(([one]) => one === kept) ? kept : formats[0]![0];
  const chooseFormat = (next: Format) => {
    setKept(next);
    write(FORMAT_KEY, next);
  };

  const native = (window as { EyeDropper?: NativeEyeDropper }).EyeDropper;
  const dropper = native
    ? () => {
        new native()
          .open()
          .then((taken) => {
            const hex = hexFromTyped(taken.sRGBHex);
            if (hex) pick(hex);
          })
          // Escape while picking is not an error.
          .catch(() => undefined);
      }
    : sources?.eyedrop
      ? () => sources.eyedrop?.((hex) => pick(hex))
      : null;
  /** The eyedropper pressed: whoever uses it knows it, and its hint is done (lib/hints). */
  const eyedrop = dropper
    ? () => {
        emitHintSignal('colour.eyedropped');
        dropper();
      }
    : null;

  // The brand's colours, each once: a colour two groups both list (Info is Cobalt) is one swatch.
  const named = (kit?.colours ?? []).flatMap((group) => group.swatches.map((swatch) => ({ name: swatch.name, hex: swatch.hex.toLowerCase() })));
  const plain = (kit?.colours ?? []).filter((group) => !group.ramp).flatMap((group) => group.swatches);
  const brandDots = plain.filter((swatch, index) => plain.findIndex((other) => other.hex.toLowerCase() === swatch.hex.toLowerCase()) === index);
  const ramps = (kit?.colours ?? []).filter((group) => group.ramp);
  const standing = showing && kit ? brandStanding(value, named) : null;
  const current = value.toLowerCase();
  const fileShown = allOfFile ? inFile : inFile.slice(0, FILE_FIRST);

  return (
    <div className="colour-panel grid grid-cols-[minmax(0,1fr)] gap-[10px] p-[12px]" onPointerUp={settle} onBlur={settle}>
      <div className="flex min-h-[26px] items-center gap-[8px]">
        {header ?? <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-label">{title}</span>}
        {header ? <span className="flex-1" /> : null}
        {onNone ? (
          <Button size="small" variant={showing ? 'plain' : 'prominent'} onClick={onNone} aria-pressed={!showing}>
            None
          </Button>
        ) : null}
        {onClose && <IconButton icon="close" label="Close colour picker" size={22} onClick={onClose} />}
      </div>

      {above}

      <Square hsv={hsv} onChange={set} />

      <div className="flex items-center gap-[8px]">
        {eyedrop ? <IconButton icon="eyedropper" label={native ? 'Take a colour from anywhere on the screen' : 'Take a colour from the page'} size={28} onClick={eyedrop} /> : null}
        <div className="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)]">
          <Slider label="Hue" value={Math.round(hsv.h)} min={0} max={360} onChange={(h) => set({ ...hsv, h })} track={hueTrack()} />
          {onAlpha ? (
            // The opacity's number sits at the end of its own bar, so the row of numbers
            // below is the colour's alone.
            <div className="flex items-center gap-[8px]">
              <Slider className="min-w-0 flex-1" label="Opacity" value={Math.round(alpha * 100)} min={0} max={100} onChange={(percent) => onAlpha(percent / 100)} track={alphaTrack(look(value))} />
              <NumberField className="colour-opacity w-[58px] shrink-0" label="Opacity, percent" value={Math.round(alpha * 100)} suffix="%" min={0} max={100} decimals={0} onChange={(percent) => onAlpha(percent / 100)} />
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex items-center gap-[6px]">
        <PopUpButton label="Colour format" value={format} options={formats} onChange={chooseFormat} className="w-[74px] shrink-0" />
        <div className="flex min-w-0 flex-1 gap-[6px]">
          {format === 'hex' ? <HexInput hex={value} onChange={give} /> : null}
          {format === 'rgb' ? <RgbFields hex={value} onChange={give} /> : null}
          {format === 'hsb' ? <HsbFields hsv={hsv} onChange={set} /> : null}
          {format === 'cmyk' && inkNow ? <InkFields ink={inkNow} onInk={giveInk} /> : null}
        </div>
      </div>

      {standing ? <Standing standing={standing} hex={look(value)} brand={kit?.name ?? 'brand'} look={look} onUse={pick} /> : null}

      {kit && brandDots.length > 0 ? (
        <div className="grid grid-cols-[minmax(0,1fr)] gap-[6px]">
          <div className="flex items-center">
            <span className="min-w-0 flex-1 truncate text-[11px] font-semibold text-label-2">{kit.name}</span>
            {ramps.length > 0 ? (
              <button
                type="button"
                aria-expanded={shades}
                onClick={() => {
                  setShades(!shades);
                  write(SHADES_KEY, !shades);
                }}
                className="shrink-0 text-[11px] font-medium text-accent-text hover:underline"
              >
                {shades ? 'Hide Shades' : 'Shades'}
              </button>
            ) : null}
          </div>
          <Dots colours={brandDots.map((swatch) => ({ hex: swatch.hex, name: swatch.name }))} current={current} look={look} onPick={pick} />
          {shades
            ? ramps.map((group) => (
                <div key={group.name} role="group" aria-label={group.name} className="flex h-[16px] overflow-hidden rounded-[6px]" style={{ boxShadow: 'inset 0 0 0 0.5px rgb(0 0 0 / 0.25)' }}>
                  {group.swatches.map((swatch) => (
                    <button
                      key={swatch.name}
                      type="button"
                      aria-label={`${swatch.name}, ${swatch.hex}`}
                      {...tip(`${swatch.name} · ${swatch.hex.toUpperCase()}`)}
                      onClick={() => pick(swatch.hex)}
                      className={`h-full flex-1 ${current === swatch.hex.toLowerCase() ? 'shadow-[inset_0_0_0_2px_var(--accent)]' : ''}`}
                      style={{ background: look(swatch.hex) }}
                    />
                  ))}
                </div>
              ))
            : null}
          {onBrandGradient && kit.gradients.length > 0 ? (
            <div className="flex gap-[6px]">
              {kit.gradients.map((gradient) => (
                <button
                  key={gradient.name}
                  type="button"
                  aria-label={gradient.name}
                  {...tip(gradient.name)}
                  onClick={() => onBrandGradient(gradient)}
                  className="h-[24px] flex-1 rounded-[7px]"
                  style={{ background: `linear-gradient(${gradient.angle}deg, ${look(gradient.from)}, ${look(gradient.to)})`, boxShadow: 'inset 0 0 0 0.5px rgb(0 0 0 / 0.25)' }}
                />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {inFile.length > 0 ? (
        <div className="grid grid-cols-[minmax(0,1fr)] gap-[6px]">
          <span className="text-[11px] font-semibold text-label-2">In This File</span>
          <Dots
            colours={fileShown.map((hex) => ({ hex }))}
            current={current}
            look={look}
            onPick={pick}
            more={!allOfFile && inFile.length > FILE_FIRST ? { count: inFile.length - FILE_FIRST, onMore: () => setAllOfFile(true) } : undefined}
          />
        </div>
      ) : null}

      {recents.length > 0 ? (
        <div className="grid grid-cols-[minmax(0,1fr)] gap-[6px]">
          <span className="text-[11px] font-semibold text-label-2">Recent</span>
          <Dots colours={recents.map((hex) => ({ hex }))} current={current} look={look} onPick={pick} />
        </div>
      ) : null}

      {onShader ? (
        <button
          type="button"
          onClick={onShader}
          className="flex items-center gap-[10px] rounded-[10px] bg-fill-2 px-[10px] py-[8px] text-left transition-colors hover:bg-fill-selected"
        >
          <span aria-hidden className="size-[26px] shrink-0 rounded-[7px]" style={{ background: 'linear-gradient(135deg, #0b3b5c, #7fd8c9)' }} />
          <span className="min-w-0">
            <span className="block text-[13px] text-label">Noise</span>
            <span className="block text-[11px] text-label-2">A shader from Lumens</span>
          </span>
        </button>
      ) : null}
    </div>
  );
}

/** Swatches as a row of small dots, each named on hover; the one in hand is ringed. */
function Dots({
  colours,
  current,
  look,
  onPick,
  more,
}: {
  colours: readonly { hex: string; name?: string }[];
  current: string;
  look: (hex: string) => string;
  onPick: (hex: string) => void;
  more?: { count: number; onMore: () => void };
}) {
  return (
    <div className="flex flex-wrap items-center gap-[5px]">
      {colours.map(({ hex, name }) => (
        <button
          key={hex}
          type="button"
          aria-label={name ? `${name}, ${hex}` : hex}
          {...tip(name ? `${name} · ${hex.toUpperCase()}` : hex.toUpperCase())}
          onClick={() => onPick(hex)}
          className={`size-[16px] shrink-0 rounded-full ${current === hex.toLowerCase() ? 'ring-[1.5px] ring-accent ring-offset-[2px] ring-offset-[var(--glass-thick)]' : ''}`}
          style={{ background: look(hex), boxShadow: 'inset 0 0 0 0.5px rgb(0 0 0 / 0.28)' }}
        />
      ))}
      {more ? (
        <button type="button" onClick={more.onMore} aria-label={`${more.count} more colours`} className="ml-[1px] text-[11px] font-medium text-label-2 hover:text-label">
          +{more.count}
        </button>
      ) : null}
    </div>
  );
}

/**
 * What the colour in hand is to the brand, said in one line: its name when it is one of the
 * brand's, "Off-brand" with the nearest brand colour one press away when it is close to
 * one, and that it is not the brand's otherwise.
 */
function Standing({
  standing,
  hex,
  brand,
  look,
  onUse,
}: {
  standing: NonNullable<ReturnType<typeof brandStanding>>;
  hex: string;
  brand: string;
  look: (hex: string) => string;
  onUse: (hex: string) => void;
}) {
  const dot = (colour: string) => <span aria-hidden className="size-[14px] shrink-0 rounded-full" style={{ background: colour, boxShadow: 'inset 0 0 0 0.5px rgb(0 0 0 / 0.28)' }} />;
  return (
    <div className="flex min-h-[30px] items-center gap-[8px] rounded-[9px] bg-fill-2 py-[3px] pl-[9px] pr-[4px] text-[12px] text-label-2">
      {standing.kind === 'brand' ? (
        <>
          {dot(hex)}
          <span className="min-w-0 flex-1 truncate">
            <b className="font-semibold text-label">{standing.name}</b> · {brand}
          </span>
        </>
      ) : standing.kind === 'near' ? (
        <>
          {dot(look(standing.hex))}
          <span className="min-w-0 flex-1 truncate">
            Off-brand. Nearest: <b className="font-semibold text-label">{standing.name}</b>
          </span>
          <Button size="small" onClick={() => onUse(standing.hex)}>
            Use
          </Button>
        </>
      ) : (
        <span className="min-w-0 flex-1 truncate">Not a {brand} colour</span>
      )}
    </div>
  );
}

/** The square's thumb, across. */
const KNOB = 16;

/** Saturation across, brightness down: the square every picker has. */
function Square({ hsv, onChange }: { hsv: Hsv; onChange: (hsv: Hsv) => void }) {
  const area = useRef<HTMLDivElement>(null);
  const pick = (event: { clientX: number; clientY: number }) => {
    const box = area.current?.getBoundingClientRect();
    if (!box) return;
    // The thumb travels inside the square, its edge at the edge, so the hand is read over that same span.
    const s = Math.min(1, Math.max(0, (event.clientX - box.left - KNOB / 2) / (box.width - KNOB)));
    const v = 1 - Math.min(1, Math.max(0, (event.clientY - box.top - KNOB / 2) / (box.height - KNOB)));
    onChange({ ...hsv, s, v });
  };

  return (
    <div
      ref={area}
      className="relative h-[128px] w-full touch-none rounded-[10px]"
      style={{
        background: `linear-gradient(to top, #000000, transparent), linear-gradient(to right, #ffffff, ${hsvToHex({ h: hsv.h, s: 1, v: 1 })})`,
        boxShadow: 'inset 0 0 0 0.5px rgb(0 0 0 / 0.25)',
      }}
      onPointerDown={(event) => {
        event.currentTarget.setPointerCapture(event.pointerId);
        // A drag across the square is one undo step (QA 2026-09-24, inspector-numbers-F10).
        beginHold();
        pick(event);
      }}
      onPointerMove={(event) => {
        if (event.buttons > 0) pick(event);
      }}
    >
      <span
        className="pointer-events-none absolute size-[16px] rounded-full"
        style={{
          // Kept inside the square: centred on the corner, pure red's thumb sat half outside it.
          left: `calc(${KNOB / 2}px + (100% - ${KNOB}px) * ${hsv.s})`,
          top: `calc(${KNOB / 2}px + (100% - ${KNOB}px) * ${1 - hsv.v})`,
          transform: 'translate(-50%, -50%)',
          background: hsvToHex(hsv),
          boxShadow: '0 0 0 2.5px #fff, 0 0 0 3.5px rgb(0 0 0 / 0.25), 0 2px 6px rgb(0 0 0 / 0.3)',
        }}
      />
    </div>
  );
}

/** The colour as hex, typed: six digits, or three doubled. */
function HexInput({ hex, onChange }: { hex: string; onChange: (hex: string) => void }) {
  const [typed, setTyped] = useState<string | null>(null);
  return (
    <div className="field-focus flex h-[26px] min-w-0 flex-1 items-center rounded-[8px] bg-fill-1 px-[8px]">
      <span className="text-[11px] text-label-3">#</span>
      <input
        aria-label="Hex"
        value={typed ?? hex.replace('#', '').toUpperCase()}
        onChange={(event) => setTyped(event.target.value)}
        onFocus={(event) => event.currentTarget.select()}
        onBlur={() => {
          // Three digits as well as six, as the Fill row's hex box reads them: FFF was
          // silently ignored here (QA 2026-09-24, paint-F19).
          const next = typed === null ? null : hexFromTyped(typed);
          if (next) onChange(next);
          setTyped(null);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
          event.stopPropagation();
        }}
        className="tnum h-full w-0 min-w-0 flex-1 bg-transparent pl-[3px] text-[12px] uppercase text-label outline-none"
      />
    </div>
  );
}

function RgbFields({ hex, onChange }: { hex: string; onChange: (hex: string) => void }) {
  const rgb = hexToRgb(hex);
  return (
    <>
      <NumberField className="min-w-0 flex-1" label="Red" prefix="R" value={rgb.r} min={0} max={255} decimals={0} onChange={(r) => onChange(rgbToHex({ ...rgb, r }))} />
      <NumberField className="min-w-0 flex-1" label="Green" prefix="G" value={rgb.g} min={0} max={255} decimals={0} onChange={(g) => onChange(rgbToHex({ ...rgb, g }))} />
      <NumberField className="min-w-0 flex-1" label="Blue" prefix="B" value={rgb.b} min={0} max={255} decimals={0} onChange={(b) => onChange(rgbToHex({ ...rgb, b }))} />
    </>
  );
}

function HsbFields({ hsv, onChange }: { hsv: Hsv; onChange: (hsv: Hsv) => void }) {
  return (
    <>
      <NumberField className="min-w-0 flex-1" label="Hue" prefix="H" value={Math.round(hsv.h)} min={0} max={360} decimals={0} onChange={(h) => onChange({ ...hsv, h })} />
      <NumberField className="min-w-0 flex-1" label="Saturation" prefix="S" value={Math.round(hsv.s * 100)} min={0} max={100} decimals={0} onChange={(s) => onChange({ ...hsv, s: s / 100 })} />
      <NumberField className="min-w-0 flex-1" label="Brightness" prefix="B" value={Math.round(hsv.v * 100)} min={0} max={100} decimals={0} onChange={(v) => onChange({ ...hsv, v: v / 100 })} />
    </>
  );
}

/** A print colour as the press is given it: C, M, Y and K, each kept exactly as typed. */
function InkFields({ ink, onInk }: { ink: Cmyk; onInk: (ink: Cmyk) => void }) {
  const percent = inkPercent(ink);
  /** One channel typed; the others as they were, to the ink's own precision. */
  const setChannel = (channel: 'c' | 'm' | 'y' | 'k', value: number) => {
    onInk({ ...ink, [channel]: inkFromPercent({ c: 0, m: 0, y: 0, k: 0, [channel]: value })[channel] });
  };
  return (
    <>
      <NumberField className="min-w-0 flex-1" label="Cyan, percent" prefix="C" value={percent.c} min={0} max={100} decimals={0} onChange={(c) => setChannel('c', c)} />
      <NumberField className="min-w-0 flex-1" label="Magenta, percent" prefix="M" value={percent.m} min={0} max={100} decimals={0} onChange={(m) => setChannel('m', m)} />
      <NumberField className="min-w-0 flex-1" label="Yellow, percent" prefix="Y" value={percent.y} min={0} max={100} decimals={0} onChange={(y) => setChannel('y', y)} />
      <NumberField className="min-w-0 flex-1" label="Black, percent" prefix="K" value={percent.k} min={0} max={100} decimals={0} onChange={(k) => setChannel('k', k)} />
    </>
  );
}

/** Typed hex as a colour — six digits, or three doubled — or null when it is not one yet. */
export function hexFromTyped(typed: string): string | null {
  const clean = typed.trim().replace(/^#?/, '#');
  if (/^#[0-9a-fA-F]{6}$/.test(clean)) return clean.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(clean)) return `#${clean.slice(1).split('').map((c) => c + c).join('')}`.toLowerCase();
  return null;
}

/** A hex as channels from 0 to 1, as a colour is stored. */
function hexToRgbUnit(hex: string): { r: number; g: number; b: number; a: number } {
  const { r, g, b } = hexToRgb(hex);
  return { r: r / 255, g: g / 255, b: b / 255, a: 1 };
}

const hueStops = () => Array.from({ length: 13 }, (_, i) => `${hsvToHex({ h: i * 30, s: 1, v: 1 })} ${(i / 12) * 100}%`).join(', ');
const hueTrack = () => `linear-gradient(to right, ${hueStops()})`;
/** Clear to the colour, over the checkerboard every editor uses for "see-through". */
const alphaTrack = (hex: string) => {
  const { r, g, b } = hexToRgb(hex);
  return `linear-gradient(to right, rgb(${r} ${g} ${b} / 0), rgb(${r} ${g} ${b} / 1)), repeating-conic-gradient(#c8c8c8 0% 25%, #ffffff 0% 50%) 0 0 / 8px 8px`;
};

function read<T>(key: string, fallback: T): T {
  try {
    const saved = localStorage.getItem(key);
    return saved ? (JSON.parse(saved) as T) : fallback;
  } catch {
    // A browser that will not keep anything still has to show a palette.
    return fallback;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Nothing to do: the colours stay for this session and no longer.
  }
}
